import { useState, useEffect, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { Event } from '@/types/event/event';
import { Price } from '@/types/price/price';
import { eventService } from '@/services/event/eventService';
import { useTenantNavigation } from '../navigation/useTenantNavigation';
import { getTenantIdFromStorage } from '@/services/authentication/supertokens';
import { WaiverItem, WaiverItemType } from '@/types/event/WaiverItem';
import { usePhoneValidation } from '../common/useInputValidation';

interface BursaryOption {
    amount: number | '';
    description: string;
}

export interface FormData {
    title: string;
    description: string;
    date: string;
    location: string;
    time_start: string;
    time_end: string;
    contact_email: string;
    contact_number: string;
    tickets_available: number;
    bursary_enabled: boolean;
    bursary_type: 'fixed' | 'custom';
    bursary_options: BursaryOption[];
    ticket_sales_active: boolean;
    waivers_enabled: boolean;
    waiver_items: WaiverItem[];
}

interface UseEventReturn {
    event: Event | null;
    loading: boolean;
    error: string | null;
    isEditing: boolean;
    showDelete: boolean;
    message: string;
    formData: FormData;
    prices: Price[];
    ticketsAvailable: number;
    ticketsAvailableByPrice: Record<string, number>;
    originalTotalTickets: Record<string, number>;
    deletedPriceIds: string[];
    errors: Record<string, string>;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    setShowDelete: Dispatch<SetStateAction<boolean>>;
    setMessage: Dispatch<SetStateAction<string>>;
    setFormData: Dispatch<SetStateAction<FormData>>;
    setPrices: Dispatch<SetStateAction<Price[]>>;
    setDeletedPriceIds: Dispatch<SetStateAction<string[]>>;
    handleEdit: () => void;
    handleCancel: () => void;
    handleUpdate: () => Promise<void>;
    handleDelete: () => Promise<void>;
    handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    handlePriceTierChange: (index: number, field: keyof Price, value: string | number) => void;
    addPrice: () => void;
    deletePrice: (index: number) => void;
    handleBursaryOptionChange: (index: number, field: keyof BursaryOption, value: string | number) => void;
    addBursaryOption: () => void;
    removeBursaryOption: (index: number) => void;
    addWaiverItem: (type: WaiverItemType) => void;
    removeWaiverItem: (index: number) => void;
    handleWaiverItemChange: (index: number, field: keyof WaiverItem, value: string) => void;
}

export function useEvent(uuid: string): UseEventReturn {
    const navigate = useNavigate();
    const { getTenantPath } = useTenantNavigation();
    const tenantId = getTenantIdFromStorage();
    const { validatePhone, filterPhoneInput } = usePhoneValidation();
    
    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [formData, setFormData] = useState<FormData>({
        title: '',
        description: '',
        date: '',
        location: '',
        time_start: '',
        time_end: '',
        contact_email: '',
        contact_number: '',
        tickets_available: 0,
        bursary_enabled: false,
        bursary_type: 'custom',
        bursary_options: [{ amount: 0, description: '' }],
        ticket_sales_active: true,
        waivers_enabled: false,
        waiver_items: []
    });
    const [prices, setPrices] = useState<Price[]>([]);
    const [ticketsAvailable, setTicketsAvailable] = useState<number>(0);
    const [ticketsAvailableByPrice, setTicketsAvailableByPrice] = useState<Record<string, number>>({});
    const [originalTotalTickets, setOriginalTotalTickets] = useState<Record<string, number>>({});
    const [deletedPriceIds, setDeletedPriceIds] = useState<string[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                if (!tenantId) {
                    setError('No organization selected');
                    setLoading(false);
                    return;
                }

                const eventData = await eventService.getEvent(uuid);
                setEvent(eventData);

                const pricesData = await eventService.getPricesForEvent(uuid);
                setPrices(pricesData);

                const originalTotals = pricesData.reduce((acc: Record<string, number>, price: Price) => ({
                    ...acc,
                    [price.uuid!]: price.tickets_available
                }), {});
                setOriginalTotalTickets(originalTotals);

                const ticketsAvailablePromises = pricesData.map((price: Price) =>
                    eventService.getTicketsAvailableForPrice(price.uuid!)
                        .then(ticketsAvailable => ({
                            [price.uuid!]: ticketsAvailable
                        }))
                        .catch(error => {
                            console.error('Error fetching tickets available for price:', error);
                            return { [price.uuid!]: 0 };
                        })
                );

                const ticketsAvailableResults = await Promise.all(ticketsAvailablePromises);
                const ticketsAvailableMap = ticketsAvailableResults.reduce((acc: Record<string, number>, curr) => ({
                    ...acc,
                    ...curr
                }), {});
                setTicketsAvailableByPrice(ticketsAvailableMap);

                const totalTicketsAvailable = Object.values(ticketsAvailableMap).reduce((sum: number, tickets: unknown) => sum + (typeof tickets === 'number' ? tickets : 0), 0);
                setTicketsAvailable(totalTicketsAvailable);

                setLoading(false);
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'An error occurred');
                setLoading(false);
            }
        };

        fetchEvent();
    }, [uuid, tenantId]);

    const handleEdit = () => {
        if (!event) return;
        
        const eventDate = new Date(event.date);
        const formattedDate = eventDate.toISOString().split('T')[0];

        setFormData({
            title: event.title || '',
            description: event.description || '',
            date: formattedDate,
            location: event.location || '',
            time_start: event.time_start || '',
            time_end: event.time_end || '',
            contact_email: event.contact_email || '',
            contact_number: event.contact_number || '',
            tickets_available: event.tickets_available || 0,
            bursary_enabled: event.bursary_enabled,
            bursary_type: event.bursary_type || 'custom',
            bursary_options: event.bursary_options || [{ amount: 0, description: '' }],
            ticket_sales_active: event.ticket_sales_active ?? true,
            waivers_enabled: event.waivers_enabled,
            waiver_items: event.waiver_items || []
        });

        setShowDelete(true);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setShowDelete(false);
        setMessage('');
        setErrors({});
        window.location.reload();
    };

    const validateForm = (): boolean => {
        let newErrors: Record<string, string> = {};
        
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (formData.description && formData.description.length > 5000) newErrors.description = "Description cannot exceed 5,000 characters";
        if (!formData.date) newErrors.date = "Date is required";
        if (!formData.location) newErrors.location = "Location is required";
        if (!formData.time_start) newErrors.time_start = "Start time is required";
        if (!formData.time_end) newErrors.time_end = "End time is required";
        if (!formData.contact_email) newErrors.contact_email = "Contact email is required";
        if (!formData.contact_number) newErrors.contact_number = "Contact number is required";

        // Validate end time is after start time
        if (formData.time_start && formData.time_end && formData.time_end <= formData.time_start) {
            newErrors.time_end = "End time must be after start time";
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.contact_email && !emailRegex.test(formData.contact_email)) {
            newErrors.contact_email = "Please enter a valid email address";
        }

        // Validate phone number format
        if (formData.contact_number) {
            const phoneValidationError = validatePhone(formData.contact_number);
            if (phoneValidationError) {
                newErrors.contact_number = phoneValidationError;
            }
        }

        // Validate ticket tiers
        if (!prices || prices.length === 0) {
            newErrors.ticket_tiers = "At least one ticket tier is required";
        } else {
            prices.forEach((price, index) => {
                if (!price.nickname) newErrors[`tier_${index}_name`] = 'Tier name is required';
                if (!price.description) newErrors[`tier_${index}_description`] = 'Description is required';
                if (isNaN(price.dollar_cost)) {
                    newErrors[`tier_${index}_price`] = 'Price is required';
                } else if (price.dollar_cost < 0) {
                    newErrors[`tier_${index}_price`] = 'Price cannot be negative';
                } else if (price.dollar_cost > 0 && price.dollar_cost < 2) {
                    newErrors[`tier_${index}_price`] = 'Non-free tickets must be at least $2';
                }
                if (isNaN(price.tickets_available)) {
                    newErrors[`tier_${index}_tickets`] = 'Tickets available is required';
                } else if (price.tickets_available <= 0) {
                    newErrors[`tier_${index}_tickets`] = 'Tickets available must be greater than 0';
                }
            });
        }

        // Validate bursary options
        if (formData.bursary_enabled) {
            if (formData.bursary_type === 'fixed' && (!formData.bursary_options || formData.bursary_options.length === 0)) {
                newErrors.bursary_options = "At least one bursary option is required";
            } else if (formData.bursary_type === 'fixed') {
                formData.bursary_options.forEach((option, index) => {
                    if (isNaN(Number(option.amount))) {
                        newErrors[`bursary_${index}_amount`] = 'Amount is required';
                    } else if (Number(option.amount) <= 0) {
                        newErrors[`bursary_${index}_amount`] = 'Amount must be greater than 0';
                    }
                    if (!option.description) newErrors[`bursary_${index}_description`] = 'Description is required';
                });
            }
        }

        // Validate todo items
        if (formData.waivers_enabled && (!formData.waiver_items || formData.waiver_items.length === 0)) {
            newErrors.waiver_items = "At least one todo item is required when todo is enabled";
        } else if (formData.waivers_enabled && formData.waiver_items) {
            formData.waiver_items.forEach((item, index) => {
                if (!item.message || item.message.trim() === '') {
                    newErrors[`waiver_${index}_message`] = 'Todo message is required';
                }
                if (item.type === 'link') {
                    if (!item.link || item.link.trim() === '') {
                        newErrors[`waiver_${index}_link`] = 'Todo link is required';
                    } else {
                        // Validate URL format
                        try {
                            const url = new URL(item.link);
                            // Check if the protocol is http or https
                            if (url.protocol !== 'http:' && url.protocol !== 'https:') {
                                newErrors[`waiver_${index}_link`] = 'Link must be a valid HTTP or HTTPS URL';
                            }
                        } catch (error) {
                            newErrors[`waiver_${index}_link`] = 'Please enter a valid URL (e.g., https://example.com)';
                        }
                    }
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpdate = async () => {
        if (!event) return;

        if (!validateForm()) return;

        const invalidPrice = prices.find(price => {
            const ticketsSold = originalTotalTickets[price.uuid!] - (ticketsAvailableByPrice[price.uuid!] ?? 0);
            return price.tickets_available < ticketsSold;
        });

        if (invalidPrice) {
            setMessage('Total tickets available cannot be lower than tickets sold');
            return;
        }

        try {
            const updatedPrices = prices.map(price => ({ ...price, unit_amount: price.dollar_cost * 100 }));

            const updateData = {
                ...formData,
                date: `${formData.date}T00:00:00.000Z`,
                org_uuid: tenantId,
                event_organizers: event.event_organizers || [],
                stripe_enabled: event.stripe_enabled || 0,
                e_transfer_enabled: event.e_transfer_enabled || 0,
                stripe_product_id: event.stripe_product_id || null,
                tickets_available: formData.tickets_available,
                deleted_prices: deletedPriceIds,
                updated_prices: updatedPrices,
                bursary_options: formData.bursary_options.map(option => ({
                    ...option,
                    amount: Number(option.amount)
                })),
                waiver_items: formData.waiver_items
            };

            await eventService.updateEvent(uuid, updateData);

            setEvent(prev => prev ? {
                ...prev,
                ...formData,
                date: `${formData.date}T00:00:00.000Z`,
                bursary_options: formData.bursary_options.map(option => ({
                    ...option,
                    amount: Number(option.amount)
                })),
                waiver_items: formData.waiver_items
            } : null);
            setMessage('');
            setErrors({});
            setIsEditing(false);
            setShowDelete(false);
            setDeletedPriceIds([]);
            
            window.location.reload();
        } catch (error: unknown) {
            console.error('Update error:', error);
            setMessage('Failed to update event. Please check your information and try again.');
        }
    };

    const handleDelete = async () => {
        try {
            await eventService.deleteEvent(uuid);
        } catch (error: unknown) {
            console.error('Delete error:', error);
            throw error; // Re-throw the error to be handled by the component
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const { name, value, type } = e.target;
        
        // Filter phone input to only allow valid characters
        const processedValue = name === 'contact_number' ? filterPhoneInput(value) : value;
        
        setFormData(prev => {
            const updatedData = {
                ...prev,
                [name]: type === 'number' ? Number(processedValue) : processedValue
            };
            
            // If bursary type is changed to 'fixed' and there are no bursary options, add one
            if (name === 'bursary_type' && processedValue === 'fixed' && prev.bursary_options.length === 0) {
                updatedData.bursary_options = [{ amount: 0, description: '' }];
            }
            
            return updatedData;
        });
        
        // Clear specific field error when changing a field
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
        });
    };

    const handlePriceTierChange = (index: number, field: keyof Price, value: string | number): void => {
        setPrices(prev => prev.map((price, i) => 
            i === index ? { 
                ...price, 
                [field]: field === 'unit_amount' || field === 'dollar_cost' || field === 'tickets_available'
                    ? typeof value === 'string' 
                        ? value === '' 
                            ? NaN 
                        : Number(value) 
                        : value 
                    : value 
            } : price
        ));
        
        // Clear ALL errors when changing any tier field
        setErrors({});
    };

    const addPrice = (): void => {
        setPrices(prev => [...prev, { uuid: undefined, nickname: '', unit_amount: 0, dollar_cost: NaN, description: '', tickets_available: NaN }]);
        setErrors({});
    };

    const deletePrice = (index: number) => {
        if (prices[index].uuid) {
            setDeletedPriceIds(prev => [...prev, prices[index].uuid!]);
        }
        setPrices(prev => prev.filter((_, i) => i !== index));
        setErrors({});
    };

    const handleBursaryOptionChange = (index: number, field: keyof BursaryOption, value: string | number): void => {
        setFormData(prev => ({
            ...prev,
            bursary_options: prev.bursary_options.map((option, i) =>
                i === index ? { 
                    ...option, 
                    [field]: field === 'amount' 
                        ? typeof value === 'string' 
                            ? value === '' 
                                ? NaN 
                                : Number(value) 
                            : value 
                        : value 
                } : option
            )
        }));
        
        // Clear ALL errors when changing any bursary option field
        setErrors({});
    };

    const addBursaryOption = (): void => {
        setFormData(prev => ({
            ...prev,
            bursary_options: [...prev.bursary_options, { amount: NaN, description: '' }]
        }));
        setErrors({});
    };

    const removeBursaryOption = (index: number) => {
        setFormData(prev => ({
            ...prev,
            bursary_options: prev.bursary_options.filter((_, i) => i !== index)
        }));
        setErrors({});
    };

    const addWaiverItem = (type: WaiverItemType) => {
        setFormData(prev => ({
            ...prev,
            waiver_items: [...prev.waiver_items, { 
                type, 
                message: '', 
                uuid: crypto.randomUUID(),
                link: type === 'link' ? '' : undefined
            }]
        }));
        // Clear todo-related errors when adding a new todo item
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.waiver_items;
            return newErrors;
        });
    };

    const removeWaiverItem = (index: number) => {
        setFormData(prev => ({
            ...prev,
            waiver_items: prev.waiver_items.filter((_, i) => i !== index)
        }));
        // Clear todo-related errors when removing a todo item
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.waiver_items;
            // Also clear any specific todo item errors
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`waiver_${index}_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });
    };

    const handleWaiverItemChange = (index: number, field: keyof WaiverItem, value: string) => {
        setFormData(prev => ({
            ...prev,
            waiver_items: prev.waiver_items.map((item, i) =>
                i === index ? { 
                    ...item, 
                    [field]: value
                } : item
            )
        }));
        // Clear specific field error when changing a todo item field
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`waiver_${index}_${field}`];
            return newErrors;
        });
    };

    return {
        event,
        loading,
        error,
        isEditing,
        showDelete,
        message,
        formData,
        prices,
        ticketsAvailable,
        ticketsAvailableByPrice,
        originalTotalTickets,
        deletedPriceIds,
        errors,
        setIsEditing,
        setShowDelete,
        setMessage,
        setFormData,
        setPrices,
        setDeletedPriceIds,
        handleEdit,
        handleCancel,
        handleUpdate,
        handleDelete,
        handleChange,
        handlePriceTierChange,
        addPrice,
        deletePrice,
        handleBursaryOptionChange,
        addBursaryOption,
        removeBursaryOption,
        addWaiverItem,
        removeWaiverItem,
        handleWaiverItemChange
    };
} 